import { useCallback, useEffect, useState } from "react";

import {
  clearStoredCredentials,
  readStoredCredentials,
  saveStoredCredentials,
} from "features/cdm/utils/pcrsCredentialStore";

// React access to the encrypted PCRS credential store. Loads (and decrypts)
// the stored credentials on mount; exposes save/clear that keep state and the
// encrypted store in sync.
const useStoredPcrsCredentialsReal = () => {
  const [credentials, setCredentials] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    let active = true;

    readStoredCredentials()
      .then((stored) => {
        if (active) setCredentials(stored);
      })
      .catch((error) => {
        console.error("Failed to read stored PCRS credentials:", error);
      })
      .finally(() => {
        if (active) setIsLoaded(true);
      });

    return () => {
      active = false;
    };
  }, []);

  const saveCredentials = useCallback(async ({ username, password }) => {
    await saveStoredCredentials({ username, password });
    setCredentials({ username, password });
  }, []);

  const clearCredentials = useCallback(() => {
    clearStoredCredentials();
    setCredentials(null);
  }, []);

  return {
    credentials,
    username: credentials?.username ?? "",
    password: credentials?.password ?? "",
    hasCredentials: !!credentials?.username && !!credentials?.password,
    isLoaded,
    saveCredentials,
    clearCredentials,
  };
};

// TEMP: pretend PCRS credentials are already stored so the CDM page never
// prompts. Flip MOCK_PCRS to false to restore the real store (preserved above).
const MOCK_PCRS = process.env.REACT_APP_CDM_MOCK_PCRS === "true";

export const useStoredPcrsCredentials = MOCK_PCRS
  ? () => ({
      credentials: { username: "mock", password: "mock" },
      username: "mock",
      password: "mock",
      hasCredentials: true,
      isLoaded: true,
      saveCredentials: async () => {},
      clearCredentials: () => {},
    })
  : useStoredPcrsCredentialsReal;